import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";
import { fetchCheckoutCoupons } from "../services/accountService";
import { WalletCoupon } from "../types/store";
import "../styles/pages/AccountPageSpacing.css";
import "../styles/pages/CouponsPage.css";

const formatCouponValue = (coupon: WalletCoupon) => {
  if (coupon.type === "ORDER_DISCOUNT") {
    return `${coupon.discountPercentage ?? 0}% OFF`;
  }

  return `₹${coupon.amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const CouponsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const openedFromCheckout = Boolean((location.state as { fromCheckout?: boolean } | null)?.fromCheckout);
  const [coupons, setCoupons] = useState<WalletCoupon[]>([]);
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState("");

  useEffect(() => {
    let active = true;
    void fetchCheckoutCoupons()
      .then((items) => {
        if (active) {
          setCoupons(items);
        }
      })
      .catch(() => {
        if (active) {
          setCoupons([]);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const applyCoupon = (code: string) => {
    const normalizedCode = code.trim();
    const match = coupons.find((coupon) => coupon.code.trim().toLowerCase() === normalizedCode.toLowerCase());
    if (!match) {
      setCouponError("Invalid coupon");
      return;
    }
    setCouponError("");
    navigate(`/cart?coupon=${encodeURIComponent(match.code)}`);
  };

  return (
    <section className="shell section page-section coupons-page">
      <div className="profile-mobile-view">
        <MobilePageHeader title="Coupons & Offers" />
      </div>

      <div className="page-header coupons-page__desktop-header">
        <div>
          <span className="eyebrow">Account</span>
          <h1>Coupons & offers</h1>
        </div>
      </div>

      <div className="coupons-page__intro">
        <h2>Use one coupon at checkout</h2>
      </div>

      {openedFromCheckout ? (
        <div className="coupons-page__entry">
          <input
            value={couponInput}
            onChange={(event) => {
              setCouponInput(event.target.value);
              setCouponError("");
            }}
            placeholder="Enter coupon code"
            aria-label="Enter coupon code"
          />
          <button type="button" onClick={() => applyCoupon(couponInput)}>
            Apply
          </button>
          {couponError ? <small>{couponError}</small> : null}
        </div>
      ) : null}

      <div className="coupons-page__grid">
        {coupons.length ? (
          coupons.map((coupon) => (
            <article className="coupons-page__card" key={coupon.id}>
              <div className="coupons-page__card-head">
                <div>
                  <span>{coupon.type.replace(/_/g, " ")}</span>
                  <h2>{coupon.code}</h2>
                </div>
                <strong>{formatCouponValue(coupon)}</strong>
              </div>
              {coupon.description ? <p>{coupon.description}</p> : <p>Available for checkout on this account.</p>}
              <div className="coupons-page__actions">
                <span>Redemption: {coupon.redemptionFrequency}</span>
                {openedFromCheckout ? (
                  <button type="button" className="button" onClick={() => applyCoupon(coupon.code)}>
                    Apply
                  </button>
                ) : null}
              </div>
            </article>
          ))
        ) : (
          <div className="coupons-page__empty-state">
            <h2>No coupons available</h2>
            <p>Admin-added coupons will appear here when they are active and assigned to your account.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default CouponsPage;
